import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getMarkersOfHarmPopupByName';
import {
    AcceptBtn,
    ContentSaferGambling,
    OverlayDefault,
    PopupDescription,
    PopupHeaderSaferGamblingClamped,
    ReviewSaferGamblingBtn,
} from 'src/domains/players/webview/components/Account/Notifications/BlockingNotifications.style';
import { UniverseComponent } from 'src/domains/common/universes';
import { useCommon } from 'src/domains/common/Common';
import { AccountState } from 'src/domains/players/state/accountState/AccountState';

interface CmsMarkersOfHarmNotificationPropsType {
    popup: Response200Type;
    onConfirm: (options?: { skipFinalRefresh?: boolean }) => Promise<void>;
}

type PopupButtonActionType = 'sign-out' | 'redirect-to-page' | 'account-suspension' | 'none';
type PopupButtonActionRawType = PopupButtonActionType | string;
type PopupButtonType = Response200Type['primary_button'] | NonNullable<Response200Type['secondary_button']>;

const redirect = (redirectUrl: string | null | undefined): void => {
    if (redirectUrl === null || redirectUrl === undefined || redirectUrl === '') {
        return;
    }

    window.location.assign(redirectUrl);
};

const normalizeAction = (action: PopupButtonActionRawType): PopupButtonActionType => {
    switch (action) {
        case 'sign-out':
        case 'sign_out':
            return 'sign-out';
        case 'redirect-to-page':
        case 'redirect_to_page':
            return 'redirect-to-page';
        case 'account-suspension':
        case 'account_suspension':
            return 'account-suspension';
        default:
            return 'none';
    }
};

export const CmsMarkersOfHarmNotification = observer(
    'CmsMarkersOfHarmNotification',
    ({ popup, onConfirm }: CmsMarkersOfHarmNotificationPropsType): JSX.Element => {
        const common = useCommon();
        const accountState = AccountState.get(common);
        const [isPrimaryLoading, setIsPrimaryLoading] = useState(false);
        const [isSecondaryLoading, setIsSecondaryLoading] = useState(false);
        const isBusy = isPrimaryLoading || isSecondaryLoading;

        const executeButtonAction = async (button: PopupButtonType): Promise<void> => {
            const action = normalizeAction(button.action);

            switch (action) {
                case 'redirect-to-page':
                    redirect(button.redirect_url);
                    return;
                case 'sign-out':
                    await accountState.handleLogout();
                    return;
                case 'account-suspension': {
                    const response = await common.trpcClient.client.accounts.suspendAccount.mutate();

                    if (response.responseStatus !== 'success') {
                        throw new Error('Failed to suspend account');
                    }

                    window.location.reload();
                    return;
                }
                case 'none':
                default:
                    window.location.reload();
                    return;
            }
        };

        const handleButtonClick = async (button: PopupButtonType): Promise<void> => {
            const action = normalizeAction(button.action);
            const skipsFinalRefresh =
                action === 'redirect-to-page' ||
                action === 'sign-out' ||
                action === 'account-suspension' ||
                action === 'none';

            await onConfirm(skipsFinalRefresh ? { skipFinalRefresh: true } : undefined);
            await executeButtonAction(button);
        };

        const handlePrimaryClick = async (): Promise<void> => {
            setIsPrimaryLoading(true);
            try {
                await handleButtonClick(popup.primary_button);
            } finally {
                setIsPrimaryLoading(false);
            }
        };

        const handleSecondaryClick = async (): Promise<void> => {
            if (popup.secondary_button === null || popup.secondary_button === undefined) {
                return;
            }

            setIsSecondaryLoading(true);
            try {
                await handleButtonClick(popup.secondary_button);
            } finally {
                setIsSecondaryLoading(false);
            }
        };

        return (
            <OverlayDefault data-test='overlay-prompt'>
                <ContentSaferGambling>
                    <UniverseComponent>
                        {(universeModule): React.ReactElement => <universeModule.compliancePopupLogo />}
                    </UniverseComponent>

                    <PopupHeaderSaferGamblingClamped title={popup.title}>{popup.title}</PopupHeaderSaferGamblingClamped>

                    <PopupDescription
                        data-test='cms-markers-of-harm-popup-description'
                        style={{
                            maxHeight: '240px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            overscrollBehavior: 'contain',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                        }}
                        dangerouslySetInnerHTML={{ __html: popup.description }}
                    />

                    <AcceptBtn
                        onClick={handlePrimaryClick}
                        size='medium'
                        isLoading={isPrimaryLoading}
                        disabled={isBusy}
                    >
                        <span
                            title={popup.primary_button.text}
                            style={{
                                display: 'block',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {popup.primary_button.text}
                        </span>
                    </AcceptBtn>

                    {popup.secondary_button === null || popup.secondary_button === undefined ? null : (
                        <ReviewSaferGamblingBtn
                            onClick={handleSecondaryClick}
                            size='medium'
                            isLoading={isSecondaryLoading}
                            disabled={isBusy}
                        >
                            <span
                                title={popup.secondary_button.text}
                                style={{
                                    display: 'block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {popup.secondary_button.text}
                            </span>
                        </ReviewSaferGamblingBtn>
                    )}
                </ContentSaferGambling>
            </OverlayDefault>
        );
    }
);
